import { Alert as MuiAlert, Snackbar } from '@mui/material';
import type { AlertColor, AlertProps } from '@mui/material/Alert';
import _ from 'lodash';
import { forwardRef, useEffect, useState } from 'react';
import React from 'react';
import type { SyntheticEvent } from 'react';
import { useLocation } from 'react-router';

import { setTime } from '../../../utils/setTime';
import { useZustand } from '../../../zustandStore';
import type { NotificationType } from '../../../zustandStore/slices';

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
	return <MuiAlert elevation={6} ref={ref} variant="outlined" {...props} />;
});

const defaultAlert: NotificationType = { type: '', message: '', onClose: _.noop, timeout: 0 };

const defaultMessages = {
	success: 'Success',
	warning: 'Warning',
	error: 'Error',
	info: 'Info',
};

const NotificationComponent = () => {
	const alerts = useZustand(state => state.notification?.alerts);
	const [alert, setAlert] = useState(defaultAlert);
	const [open, setOpen] = useState(false);
	const location = useLocation();

	useEffect(() => {
		if (alerts.length === 0) {
			return;
		}

		const latestAlert = alerts.at(-1) || defaultAlert;
		setAlert({
			...latestAlert,
			onClose: latestAlert.onClose || _.noop,
			timeout: latestAlert.timeout || setTime(3, 'seconds'),
		});
		setOpen(true);

		return () => {
			setAlert(defaultAlert);
			setOpen(false);
		};
	}, [alerts]);

	useEffect(() => {
		// we want to close the notification when the location changes
		setAlert(defaultAlert);
		setOpen(false);
	}, [location]);

	const handleClose = (_event?: SyntheticEvent | Event, reason?: string) => {
		if (reason === 'clickaway') {
			return;
		}
		if (typeof alert.onClose === 'function') {
			alert.onClose();
		}
		setAlert(defaultAlert);
		setOpen(false);
	};

	return (
		<Snackbar
			data-testid="notification"
			open={open}
			autoHideDuration={
				window['IS_PLAYWRIGHT_RUNNING'] ? setTime(10, 'seconds') : alert.timeout || setTime(3, 'seconds')
			}
			onClose={handleClose}
			anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
		>
			<Alert severity={alert.type as AlertColor} ts-variant="notification">
				{alert.message != null && (typeof alert.message === 'string' || React.isValidElement(alert.message))
					? alert.message
					: defaultMessages[alert.type] || ''}
			</Alert>
		</Snackbar>
	);
};

export default NotificationComponent;
